import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  History,
  Stethoscope,
  UserRound,
  UserX,
} from 'lucide-react';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { getCurrentUserId } from 'common-src/features/auth';
import { setClientsCached, setOrganizationsCached } from 'common-src/features/cache';
import { getClientsRequest, getDashboardClients } from 'common-src/models/Client';
import { getOrganizationsRequest } from 'common-src/models/Organization';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';

import {
  copyTextToClipboard,
  formatClinicianName,
  formatClock12,
  getAppointmentTypeColor,
  getDayHeadingLabel,
  getProviderInitials,
} from '../../helpers';
import {
  fetchAppointmentBriefs,
  fetchAppointmentChanges,
  fetchProviderHudAppointments,
} from '../../services/careOrchestrationApi';
import BookingRail from '../BookingRail';
import DayChip from '../DayChip/DayChip';
import AppointmentBrief, {
  CaregiverLine,
  CognitionSection,
  EarlierVisitsSection,
  IntakeSection,
  NeedsAttentionSection,
  NotOnFileSection,
  PatientSummarySection,
  SeenBeforeSection,
  usePatientSummary,
  VisitDetailsSection,
} from './AppointmentBrief';
import dayStripStyles from './DayStrip.module.scss';
import {
  getApptDay,
  getApptTime,
  getDayDigest,
  getOpeningVisit,
  getSeenBefore,
  isBehind,
  slugify,
  sortByTime,
} from './helpers';
import cardStyles from './HudAppointmentCard.module.scss';
import styles from './ProviderHudView.module.scss';
import pickerStyles from './ProviderPicker.module.scss';

const FAILED_BRIEF = {
  mocaResults: [],
  visitNote: null,
  earlierVisits: [],
  patientSummary: null,
  intake: null,
  failedSections: ['all'],
};

const NARROW_QUERY = '(max-width: 767px)';

const useHudLayout = () => {
  const [searchParams] = useSearchParams();
  const isPanel = searchParams.get('layout') === 'panel';
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(NARROW_QUERY);
    const handleChange = (event) => setIsNarrow(event.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return { isPanel, oneColumn: isPanel || isNarrow };
};

const BRIEF_BATCH_SIZE = 25;

const useBriefCache = () => {
  const [briefs, setBriefs] = useState({});
  const inFlight = useRef(new Set());

  const load = useCallback((ids) => {
    const wanted = ids.filter((id) => !inFlight.current.has(id));
    if (!wanted.length) return;
    wanted.forEach((id) => inFlight.current.add(id));
    for (let start = 0; start < wanted.length; start += BRIEF_BATCH_SIZE) {
      const chunk = wanted.slice(start, start + BRIEF_BATCH_SIZE);
      fetchAppointmentBriefs(chunk)
        .catch(() => ({}))
        .then((rows) =>
          setBriefs((prev) => ({
            ...prev,
            ...Object.fromEntries(chunk.map((id) => [id, rows[id] ?? FAILED_BRIEF])),
          })),
        )
        .finally(() => chunk.forEach((id) => inFlight.current.delete(id)));
    }
  }, []);

  const drop = useCallback((ids) => {
    if (!ids.length) return;
    setBriefs((prev) => {
      const next = { ...prev };
      ids.forEach((id) => delete next[id]);
      return next;
    });
  }, []);

  return { briefs, load, drop };
};

const formatVisits = (count) => {
  if (count == null) return '';
  if (count === 0) return 'No visits';
  return `${count} ${count === 1 ? 'visit' : 'visits'}`;
};

const ProviderPicker = ({ providers, providerId, counts, onSelect, panel = false }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const current = providers.find((provider) => provider.id === providerId);

  return (
    <div className={`${pickerStyles.root} ${panel ? pickerStyles.panel : ''}`}>
      <span className={pickerStyles.label}>Schedule for</span>
      <div ref={rootRef} className={pickerStyles.picker}>
        <button
          type="button"
          className={pickerStyles.trigger}
          onClick={() => setOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={!providers.length}
        >
          {current ? (
            <>
              <span className={pickerStyles.avatar} aria-hidden="true">
                {getProviderInitials(current.name)}
              </span>
              <span className={pickerStyles.name}>{current.name}</span>
              <span className={pickerStyles.count}>{formatVisits(counts?.[current.id])}</span>
            </>
          ) : (
            <span className={pickerStyles.count}>No providers</span>
          )}
          <ChevronDown size={16} className={pickerStyles.chevron} aria-hidden="true" />
        </button>

        {open && (
          <div className={pickerStyles.dropdown} role="listbox">
            {providers.map((provider) => {
              const isActive = provider.id === providerId;
              return (
                <button
                  key={provider.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`${pickerStyles.option} ${isActive ? pickerStyles.optionActive : ''}`}
                  onClick={() => {
                    onSelect(provider.id);
                    setOpen(false);
                  }}
                >
                  <span className={pickerStyles.check}>
                    {isActive && <Check size={14} aria-hidden="true" />}
                  </span>
                  <span className={pickerStyles.avatar} aria-hidden="true">
                    {getProviderInitials(provider.name)}
                  </span>
                  <span className={pickerStyles.optionName}>{provider.name}</span>
                  <span className={pickerStyles.count}>{formatVisits(counts?.[provider.id])}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

ProviderPicker.propTypes = {
  providers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  providerId: PropTypes.string,
  counts: PropTypes.objectOf(PropTypes.number),
  panel: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

const parseDay = (dayKey) => {
  const [year, month, date] = dayKey.split('-').map(Number);
  return new Date(year, month - 1, date);
};

const formatChip = (dayKey) =>
  `${parseDay(dayKey).toLocaleDateString('en-US', { weekday: 'short' })} ${Number(dayKey.slice(8))}`;

const formatMonthDay = (dayKey) =>
  parseDay(dayKey).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const formatFullDay = (dayKey) =>
  parseDay(dayKey).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

const isTyping = (el) =>
  !!el &&
  (el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    el.isContentEditable);

const DayStrip = ({ days, day, today, rangeFrom, rangeTo, isLoading, onSelect, panel = false }) => {
  const chipsRef = useRef(null);
  const keys = days.map((d) => d.day);
  const index = keys.indexOf(day);
  const prev = index > 0 ? keys[index - 1] : null;
  const next = index >= 0 && index < keys.length - 1 ? keys[index + 1] : null;
  const splitAt = keys.findIndex((key) => key >= today);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(document.activeElement)) return;
      const target = event.key === 'ArrowLeft' ? prev : event.key === 'ArrowRight' ? next : null;
      if (!target) return;
      event.preventDefault();
      onSelect(target);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prev, next, onSelect]);

  useEffect(() => {
    if (!panel) return;
    chipsRef.current
      ?.querySelector('[aria-pressed="true"]')
      ?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [panel, day, days.length]);

  let summary = 'Loading visits...';
  if (!isLoading) {
    const count = days.length;
    summary = `${formatMonthDay(rangeFrom)} – ${formatMonthDay(rangeTo)} · ${count} ${
      count === 1 ? 'day' : 'days'
    } with visits`;
  }

  return (
    <div className={`${dayStripStyles.root} ${panel ? dayStripStyles.panel : ''}`}>
      <button
        type="button"
        className={dayStripStyles.arrow}
        aria-label="Previous day with visits"
        title={'Previous day with visits (\u2190)'}
        disabled={!prev}
        onClick={() => prev && onSelect(prev)}
      >
        <ChevronLeft size={15} aria-hidden="true" />
      </button>

      {days.length > 0 && (
        <div
          ref={chipsRef}
          role="group"
          aria-label="Days with visits"
          className={dayStripStyles.chips}
        >
          {days.map((d, i) => {
            const selected = d.day === day;
            const isToday = d.day === today;
            return (
              <Fragment key={d.day}>
                {i === splitAt && i > 0 && (
                  <span className={dayStripStyles.hairline} aria-hidden="true" />
                )}
                <DayChip
                  label={isToday ? 'Today' : formatChip(d.day)}
                  count={d.count}
                  selected={selected}
                  pill
                  compact
                  ariaLabel={`${formatFullDay(d.day)}${isToday ? ' (today)' : ''}, ${d.count} ${
                    d.count === 1 ? 'visit' : 'visits'
                  }`}
                  title={formatFullDay(d.day)}
                  onClick={() => onSelect(d.day)}
                />
              </Fragment>
            );
          })}
        </div>
      )}

      <button
        type="button"
        className={dayStripStyles.arrow}
        aria-label="Next day with visits"
        title={'Next day with visits (\u2192)'}
        disabled={!next}
        onClick={() => next && onSelect(next)}
      >
        <ChevronRight size={15} aria-hidden="true" />
      </button>

      <span className={dayStripStyles.summary}>{summary}</span>
    </div>
  );
};

DayStrip.propTypes = {
  days: PropTypes.arrayOf(
    PropTypes.shape({ day: PropTypes.string.isRequired, count: PropTypes.number.isRequired }),
  ).isRequired,
  day: PropTypes.string,
  today: PropTypes.string.isRequired,
  rangeFrom: PropTypes.string.isRequired,
  rangeTo: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  panel: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

const SEEN_ICON = { seen: History, new: UserRound, none: UserX };
const SEEN_CLASS = {
  seen: cardStyles.toneSeen,
  new: cardStyles.toneNew,
  none: cardStyles.toneNone,
};

const HudAppointmentCard = ({ appt, todayKey, selected, condensed = false, onSelect }) => {
  const statusBadge =
    appt?.statusLabel && getApptDay(appt) < todayKey ? (
      <span className={cardStyles.status}>{appt.statusLabel}</span>
    ) : null;

  if (condensed) {
    return (
      <div className={cardStyles.row}>
        <span className={cardStyles.condensedTime}>{formatClock12(getApptTime(appt))}</span>
        <button
          type="button"
          aria-pressed={selected}
          data-appointment-id={appt?.id}
          className={`${cardStyles.condensedCard} ${selected ? cardStyles.cardSelected : ''}`}
          onClick={onSelect}
        >
          <span
            className={cardStyles.dot}
            style={{ background: getAppointmentTypeColor(appt).dot }}
            aria-hidden="true"
          />
          <span className={cardStyles.condensedName}>{appt?.patient?.name}</span>
          <span className={cardStyles.condensedType}>{appt?.visitType}</span>
          {statusBadge}
        </button>
      </div>
    );
  }

  const seen = getSeenBefore(appt, todayKey);
  const SeenIcon = SEEN_ICON[seen.tone];
  const age = appt?.patient?.age;

  return (
    <div className={cardStyles.row}>
      <BookingRail
        time={getApptTime(appt)}
        durationMinutes={appt?.durationMinutes}
        appointmentTypeId={appt?.appointmentTypeId}
      />
      <button
        type="button"
        aria-pressed={selected}
        data-appointment-id={appt?.id}
        className={`${cardStyles.card} ${selected ? cardStyles.cardSelected : ''}`}
        onClick={onSelect}
      >
        <span className={cardStyles.nameLine}>
          <span className={cardStyles.name}>{appt?.patient?.name}</span>
          {age != null && <span className={cardStyles.age}>{age}</span>}
          {statusBadge}
        </span>
        <span className={cardStyles.visitLine}>
          <span
            className={cardStyles.dot}
            style={{ background: getAppointmentTypeColor(appt).dot }}
            aria-hidden="true"
          />
          {appt?.visitType}
        </span>
        <CaregiverLine appt={appt} />
        <span className={`${cardStyles.seen} ${SEEN_CLASS[seen.tone]}`}>
          <SeenIcon size={13} className={cardStyles.seenIcon} aria-hidden="true" />
          {seen.label}
        </span>
      </button>
    </div>
  );
};

HudAppointmentCard.propTypes = {
  appt: PropTypes.shape({
    id: PropTypes.string,
    durationMinutes: PropTypes.number,
    appointmentTypeId: PropTypes.string,
    visitType: PropTypes.string,
    providerAthenaId: PropTypes.string,
    seenByProvider: PropTypes.bool,
    lastVisit: PropTypes.object,
    lastVisitWithProvider: PropTypes.object,
    caregivers: PropTypes.arrayOf(PropTypes.object),
    statusLabel: PropTypes.string,
    patient: PropTypes.shape({ name: PropTypes.string, age: PropTypes.number }),
  }).isRequired,
  todayKey: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  condensed: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

const WINDOW_DAYS = 7;
const NO_RESULTS = [];

const formatNow = (date) =>
  `${date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;

const toDateOnly = (date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const getWindowRange = (todayKey) => {
  const [year, month, day] = todayKey.split('-').map(Number);
  const from = new Date(year, month - 1, day - WINDOW_DAYS);
  const to = new Date(year, month - 1, day + WINDOW_DAYS);
  return { dateFrom: toDateOnly(from), dateTo: toDateOnly(to) };
};

function ProviderHudView({ onBack, departmentLabelById }) {
  const [now, setNow] = useState(() => new Date());
  const [copied, setCopied] = useState(null);
  const [expandedSeen, setExpandedSeen] = useState({});
  const [appointments, setAppointments] = useState(null);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { isPanel, oneColumn } = useHudLayout();

  useEffect(() => {
    if (!isPanel) return;
    dispatch(
      getOrganizationsRequest({
        successBlock: () =>
          dispatch(
            getClientsRequest({
              successBlock: () => {
                dispatch(setOrganizationsCached(true));
                dispatch(setClientsCached(true));
              },
            }),
          ),
      }),
    );
  }, [isPanel, dispatch]);
  const [briefOpen, setBriefOpen] = useState(!!searchParams.get('appt'));
  const [selectedProviderId, setSelectedProviderId] = useState(searchParams.get('provider'));
  const [selectedDay, setSelectedDay] = useState(searchParams.get('date'));
  const [selectedApptId, setSelectedApptId] = useState(searchParams.get('appt'));

  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const currentUserId = useCustomSelector(getCurrentUserId);
  const currentUser = useCustomSelector((state) =>
    singleModelSelector(state, 'Client', currentUserId),
  );

  const providers = useMemo(() => {
    const base = clients
      .filter((client) => client?.athenaProviderId != null && client.athenaProviderId !== '')
      .map((client) => ({
        id: `${client.athenaProviderId}`.trim(),
        name: formatClinicianName(client),
        slug: slugify(`${client.firstName ?? ''} ${client.lastName ?? ''}`),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const slugCounts = {};
    base.forEach((provider) => {
      slugCounts[provider.slug] = (slugCounts[provider.slug] || 0) + 1;
    });
    return base.map((provider) =>
      slugCounts[provider.slug] > 1
        ? { ...provider, slug: `${provider.slug}-${provider.id}` }
        : provider,
    );
  }, [clients]);

  const currentUserProviderId =
    currentUser?.athenaProviderId != null ? `${currentUser.athenaProviderId}`.trim() : null;
  const requestedSlug = slugify(selectedProviderId);
  const requestedProvider = selectedProviderId
    ? providers.find(
        (provider) => provider.id === selectedProviderId || provider.slug === requestedSlug,
      )
    : null;
  const providerId =
    requestedProvider?.id ??
    (providers.some((provider) => provider.id === currentUserProviderId)
      ? currentUserProviderId
      : (providers[0]?.id ?? null));

  const todayKey = toDateOnly(now);
  const sinceRef = useRef(null);
  const { briefs, load: loadBriefs, drop: dropBriefs } = useBriefCache();

  useEffect(() => {
    let cancelled = false;
    setAppointments(null);
    sinceRef.current = new Date(Date.now() - 2 * 60 * 1000).toISOString();
    fetchProviderHudAppointments(getWindowRange(todayKey))
      .then((rows) => {
        if (!cancelled) setAppointments(rows);
      })
      .catch(() => {
        if (!cancelled) setAppointments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [todayKey]);

  const counts = useMemo(() => {
    if (!appointments) return undefined;
    const byProvider = Object.fromEntries(providers.map((provider) => [provider.id, 0]));
    appointments.forEach((appointment) => {
      const id = `${appointment.providerAthenaId ?? ''}`.trim();
      if (id in byProvider) byProvider[id] += 1;
    });
    return byProvider;
  }, [appointments, providers]);

  const range = useMemo(() => getWindowRange(todayKey), [todayKey]);

  const providerAppointments = useMemo(
    () =>
      (appointments || []).filter(
        (appointment) => `${appointment.providerAthenaId ?? ''}`.trim() === providerId,
      ),
    [appointments, providerId],
  );

  const days = useMemo(() => {
    const countsByDay = {};
    providerAppointments.forEach((appointment) => {
      const dayKey = getApptDay(appointment);
      if (dayKey) countsByDay[dayKey] = (countsByDay[dayKey] || 0) + 1;
    });
    return Object.keys(countsByDay)
      .sort()
      .map((dayKey) => ({ day: dayKey, count: countsByDay[dayKey] }));
  }, [providerAppointments]);

  const dayKeys = days.map((d) => d.day);
  const defaultDay = dayKeys.includes(todayKey)
    ? todayKey
    : (dayKeys.find((key) => key > todayKey) ?? dayKeys[dayKeys.length - 1] ?? null);
  const day = dayKeys.includes(selectedDay) ? selectedDay : defaultDay;

  const dayRows = useMemo(
    () => sortByTime(providerAppointments.filter((appointment) => getApptDay(appointment) === day)),
    [providerAppointments, day],
  );
  const selectedAppt =
    dayRows.find((appointment) => appointment.id === selectedApptId) ?? getOpeningVisit(dayRows);
  const brief = selectedAppt ? briefs[selectedAppt.id] : undefined;

  const patientSummary =
    brief && !brief.failedSections.some((name) => name === 'all' || name === 'patient_summary')
      ? (brief.patientSummary ?? null)
      : undefined;

  const patientSummaryActions = usePatientSummary({
    appt: selectedAppt,
    summary: patientSummary,
    onUploaded: () => loadBriefs([selectedAppt.id]),
  });

  useEffect(() => {
    if (selectedAppt && !(selectedAppt.id in briefs)) loadBriefs([selectedAppt.id]);
    loadBriefs(dayRows.map((row) => row.id).filter((id) => !(id in briefs)));
  }, [briefs, dayRows, loadBriefs, selectedAppt?.id]);

  useInterval(async () => {
    if (!providerId || appointments === null || !sinceRef.current) return;
    try {
      const changes = await fetchAppointmentChanges({
        since: sinceRef.current,
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
        providerAthenaId: providerId,
      });
      const changed = changes?.changedAppointmentIds ?? [];
      const removed = changes?.removedAppointmentIds ?? [];
      if (changed.length || removed.length) {
        setAppointments(await fetchProviderHudAppointments(range));
        dropBriefs(removed);
        loadBriefs(changed);
      }
      if (changes?.serverTime) sinceRef.current = changes.serverTime;
    } catch (e) {
      // the next tick tries again from the same point
    }
  }, 60 * 1000);

  const mocaResults = brief?.mocaResults ?? NO_RESULTS;
  const visitNote = brief?.visitNote ?? null;
  const earlierVisits = brief?.earlierVisits ?? NO_RESULTS;
  const intake = brief && !brief.failedSections.includes('intake') ? brief.intake : undefined;
  const providerName = providers.find((provider) => provider.id === providerId)?.name ?? '';

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(document.activeElement)) return;
      const step = { j: 1, J: 1, k: -1, K: -1 }[event.key];
      if (!step || !selectedAppt) return;
      const target = dayRows[dayRows.findIndex((row) => row.id === selectedAppt.id) + step];
      if (!target) return;
      event.preventDefault();
      setSelectedApptId(target.id);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dayRows, selectedAppt]);

  useEffect(() => {
    if (!selectedAppt) return;
    document
      .querySelector(`[data-appointment-id="${selectedAppt.id}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedAppt?.id]);

  useInterval(() => setNow(new Date()), 60 * 1000);

  useEffect(() => {
    if (!providerName) return undefined;
    const previousTitle = document.title;
    document.title = `${providerName} \u00b7 Today`;
    return () => {
      document.title = previousTitle;
    };
  }, [providerName]);

  const handleCopyLink = async (kind) => {
    const providerSlug = providers.find((provider) => provider.id === providerId)?.slug;
    const url =
      kind === 'home'
        ? `${window.location.origin}/provider-hud?${new URLSearchParams({ provider: providerSlug })}`
        : `${window.location.origin}${window.location.pathname}?${new URLSearchParams({
            provider: providerId,
            ...(day ? { date: day } : {}),
            ...(selectedAppt ? { appt: selectedAppt.id } : {}),
          })}`;
    if (!(await copyTextToClipboard(url))) return;
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
  };

  const bodyRef = useRef(null);

  const detailRef = useRef(null);

  useEffect(() => {
    if (oneColumn) bodyRef.current?.scrollTo({ top: 0 });
    else detailRef.current?.scrollTo({ top: 0 });
  }, [briefOpen, selectedAppt?.id, oneColumn]);

  const listView = (
    <>
      <header>
        <h2 className={styles.dayHeading}>{getDayHeadingLabel(todayKey, day)}</h2>
        <p className={styles.digest}>{getDayDigest(providerName, dayRows)}</p>
      </header>
      <ol className={styles.cards}>
        {dayRows.map((appointment) => (
          <li key={appointment.id}>
            <HudAppointmentCard
              appt={appointment}
              todayKey={todayKey}
              selected={appointment.id === selectedAppt?.id}
              condensed={isBehind(appointment)}
              onSelect={() => {
                setSelectedApptId(appointment.id);
                setBriefOpen(true);
              }}
            />
          </li>
        ))}
      </ol>
      <p className={styles.hint}>{'\u2190 \u2192 change day \u00b7 J K change visit'}</p>
    </>
  );

  const briefView = selectedAppt ? (
    <AppointmentBrief
      appt={selectedAppt}
      departmentLabelById={departmentLabelById}
      mocaResults={mocaResults}
      patientSummaryActions={patientSummaryActions}
      todayKey={todayKey}
    >
      <NeedsAttentionSection appt={selectedAppt} intake={intake} todayKey={todayKey} />
      <PatientSummarySection actions={patientSummaryActions} todayKey={todayKey} />
      <SeenBeforeSection
        appt={selectedAppt}
        note={visitNote}
        todayKey={todayKey}
        expanded={!!expandedSeen[selectedAppt.id]}
        onToggle={() =>
          setExpandedSeen((prev) => ({ ...prev, [selectedAppt.id]: !prev[selectedAppt.id] }))
        }
      />
      <CognitionSection
        patientId={selectedAppt.patientId}
        patientName={selectedAppt.patient?.name}
        results={mocaResults}
        isLoading={brief === undefined}
        todayKey={todayKey}
      />
      <IntakeSection appt={selectedAppt} intake={intake} todayKey={todayKey} />
      <EarlierVisitsSection
        appt={selectedAppt}
        visits={earlierVisits}
        isLoading={brief === undefined}
        todayKey={todayKey}
      />
      <NotOnFileSection
        results={mocaResults}
        intake={intake}
        patientSummary={patientSummary}
        isInitialVisit={/new pt/i.test(selectedAppt.visitType ?? '')}
        isLoading={brief === undefined}
      />
      <VisitDetailsSection appt={selectedAppt} />
      {patientSummaryActions.overlays}
    </AppointmentBrief>
  ) : null;

  const visitIndex = dayRows.findIndex((row) => row.id === selectedAppt?.id);
  const visitBar = (
    <div className={styles.visitBar}>
      <button type="button" className={styles.visitBarBack} onClick={() => setBriefOpen(false)}>
        <ArrowLeft size={15} aria-hidden="true" />
        All visits
      </button>
      <div className={styles.visitStep}>
        <button
          type="button"
          className={styles.stepButton}
          aria-label="Previous visit"
          disabled={visitIndex <= 0}
          onClick={() => setSelectedApptId(dayRows[visitIndex - 1].id)}
        >
          <ChevronLeft size={15} aria-hidden="true" />
        </button>
        <span className={styles.stepCount}>{`${visitIndex + 1} of ${dayRows.length}`}</span>
        <button
          type="button"
          className={styles.stepButton}
          aria-label="Next visit"
          disabled={visitIndex < 0 || visitIndex >= dayRows.length - 1}
          onClick={() => setSelectedApptId(dayRows[visitIndex + 1].id)}
        >
          <ChevronRight size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  const fullHudHref = `${window.location.origin}${window.location.pathname}?${new URLSearchParams({
    provider: providerId ?? '',
  })}`;

  return (
    <div className={`${styles.root} ${isPanel ? styles.panel : ''}`}>
      {isPanel ? (
        <header className={styles.header}>
          <div className={styles.headerInner}>
            <p className={styles.subtitle}>Today is {formatNow(now)}</p>
            <a
              href={fullHudHref}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.copyButton}
            >
              Open full HUD
              <ExternalLink size={14} aria-hidden="true" />
            </a>
          </div>
        </header>
      ) : (
        <header className={styles.header}>
          <div className={styles.headerInner}>
            <button type="button" className={styles.backButton} onClick={onBack}>
              <ArrowLeft size={15} aria-hidden="true" />
              Orchestration Manager
            </button>
            <div className={styles.divider} aria-hidden="true" />
            <div className={styles.logo} aria-hidden="true">
              <Stethoscope size={15} />
            </div>
            <div>
              <h1 className={styles.title}>Provider HUD</h1>
              <p className={styles.subtitle}>Today is {formatNow(now)}</p>
            </div>
            <div className={styles.copyActions}>
              {[
                { kind: 'visit', label: 'Copy link' },
                { kind: 'home', label: 'Copy home link' },
              ].map(({ kind, label }) => (
                <button
                  key={kind}
                  type="button"
                  className={styles.copyButton}
                  disabled={!providerId}
                  onClick={() => handleCopyLink(kind)}
                >
                  {copied === kind ? (
                    <Check size={14} aria-hidden="true" />
                  ) : (
                    <Copy size={14} aria-hidden="true" />
                  )}
                  {copied === kind ? 'Copied' : label}
                </button>
              ))}
            </div>
          </div>
        </header>
      )}
      <div className={styles.body} ref={bodyRef}>
        <div className={styles.bodyInner}>
          <ProviderPicker
            providers={providers}
            providerId={providerId}
            counts={counts}
            panel={isPanel}
            onSelect={(id) => {
              setSelectedProviderId(id);
              setSelectedDay(null);
              setSelectedApptId(null);
              setBriefOpen(false);
            }}
          />
          <DayStrip
            days={days}
            day={day}
            today={todayKey}
            rangeFrom={range.dateFrom}
            rangeTo={range.dateTo}
            isLoading={appointments === null}
            panel={isPanel}
            onSelect={(dayKey) => {
              setSelectedDay(dayKey);
              setSelectedApptId(null);
              setBriefOpen(false);
            }}
          />

          {day &&
            (oneColumn ? (
              briefOpen && selectedAppt ? (
                <div className={styles.oneColumn}>
                  {visitBar}
                  {briefView}
                </div>
              ) : (
                <div className={styles.list}>{listView}</div>
              )
            ) : (
              <div className={styles.columns}>
                <aside className={styles.list}>{listView}</aside>
                <main className={styles.detail} ref={detailRef}>
                  {briefView}
                </main>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

ProviderHudView.propTypes = {
  onBack: PropTypes.func.isRequired,
  departmentLabelById: PropTypes.object,
};

export default ProviderHudView;
